import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { payrollAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Loader2, Download } from 'lucide-react';
import { formatIndianCurrency } from '../../lib/utils';

const MyPayslips = () => {
  const { user } = useAuth();
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayslips();
  }, []);

  const fetchPayslips = async () => {
    try {
      const response = await payrollAPI.getMyPayslips();
      setPayslips(response.data);
    } catch (error) {
      toast.error('Failed to fetch payslips');
    } finally {
      setLoading(false);
    }
  };

  const downloadPayslip = async (runId) => {
    try {
      const response = await payrollAPI.downloadPayslipPdf(user.employee_id, runId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payslip_${runId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download payslip');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">My Payslips</h1>
        <p className="text-muted-foreground">View and download your payslips</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Payslip History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pay Period</TableHead>
                <TableHead>Gross Salary</TableHead>
                <TableHead>TDS</TableHead>
                <TableHead>Net Salary</TableHead>
                <TableHead className="text-right">Download</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payslips.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-10 text-muted-foreground">
                    No payslips found
                  </TableCell>
                </TableRow>
              ) : (
                payslips.map((payslip) => (
                  <TableRow key={payslip.run_id}>
                    <TableCell className="font-medium">{payslip.pay_period}</TableCell>
                    <TableCell>{formatIndianCurrency(payslip.gross_salary)}</TableCell>
                    <TableCell className="text-red-600">{formatIndianCurrency(payslip.tds)}</TableCell>
                    <TableCell className="font-semibold">{formatIndianCurrency(payslip.net_salary)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadPayslip(payslip.run_id)}
                        data-testid={`download-payslip-${payslip.run_id}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyPayslips;
